import {useCallback, useEffect, useRef, useState} from "react";
import {AppState} from "react-native";

export const DEFAULT_PAYOUT_RESEND_COOLDOWN_SECONDS = 60;
export const MAX_PAYOUT_RESEND_COOLDOWN_SECONDS = 60 * 60;

const resolvePayoutResendCooldown = (value?: number) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > MAX_PAYOUT_RESEND_COOLDOWN_SECONDS) {
    return DEFAULT_PAYOUT_RESEND_COOLDOWN_SECONDS;
  }

  return Math.ceil(value);
};

export const usePayoutResendCooldown = () => {
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const deadlineRef = useRef<number>();
  const isActive = secondsRemaining > 0;

  const syncCooldown = useCallback(() => {
    const deadline = deadlineRef.current;
    if (!deadline) return;

    const nextSecondsRemaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
    if (nextSecondsRemaining === 0) deadlineRef.current = undefined;
    setSecondsRemaining(nextSecondsRemaining);
  }, []);

  useEffect(() => {
    if (!isActive) return undefined;

    const timeout = setTimeout(syncCooldown, 1000);
    return () => clearTimeout(timeout);
  }, [isActive, secondsRemaining, syncCooldown]);

  useEffect(() => {
    if (!isActive) return undefined;

    const subscription = AppState.addEventListener('change', nextState => {
      if (nextState === 'active') syncCooldown();
    });
    return () => subscription.remove();
  }, [isActive, syncCooldown]);

  const startCooldown = useCallback((value?: number) => {
    const seconds = resolvePayoutResendCooldown(value);
    deadlineRef.current = seconds > 0 ? Date.now() + seconds * 1000 : undefined;
    setSecondsRemaining(seconds);
  }, []);

  const resetCooldown = useCallback(() => {
    deadlineRef.current = undefined;
    setSecondsRemaining(0);
  }, []);

  return {secondsRemaining, startCooldown, resetCooldown};
};
